import axios from "axios"
import packageJson from '../../../package.json';
import { basename } from "../../globals_VITE";
import { Origin } from "../../api/data/originApi";
import { OriginType } from "../../api/data/originTypeApi";

export interface Balance {
    value: number;
    crn: string;
    startDate?: Date;
    endDate?: Date;
}

export interface BasePrice {
    crn: string;
    value: number;
}

export interface CameraCatg {
    type: string;
    coeff: number;
}

export interface DiscountPrepayment {
    id: number;
    from: number;
    to: number;
    value_pc: number;
}

export interface DiscountQuantity {
    id: number;
    from: number;
    to: number;
    value_pc: number;
}

const CameraAnalyticsType = {
  ON_BOARD: 2,
  SYSTEM: 1,
} as const;

const getBalance = async (): Promise<Balance | null> => {
    const res = await axios.get(basename + '/billing/balance/');
    if (res.data && res.data.length > 0) {
        return {
            value: res.data[0].value,
            crn: res.data[0].crn,
            startDate: new Date(res.data[0].start_date),
            endDate: new Date(res.data[0].end_date),
        };
    } else {
        return null;
    }
}

const getCameras = async (param: {analytics_type: number}): Promise<number> => {
    const res1 = await axios.get<OriginType[]>('pcnt/origin_type');
    
    // data to map of origin_type_id to analytics_type
    const originTypeMap: Record<number, number> = {};
    res1.data.forEach(item => {
        originTypeMap[item.origin_type_id] = item.analytics_type;
    });

    const res = await axios.get<Origin[]>('pcnt/v_customer_origin');
    if (res.data) {
        return res.data
            .filter((origin) => originTypeMap[origin.origin_type_id] === param.analytics_type && origin.is_enabled)
            .length;
    } else {
        return 0;
    }
}

const getOrderStatus = async (orderId: number): Promise<any> => {
    try {
        const res = await axios.get(basename + `/billing/pay_status/?order_id=${orderId}`);
        return res.data ? res.data : null;
    } catch {
        return null;
    }
}

const getBasePrice = async (): Promise<BasePrice[]> => {
    const res = await axios.get(basename + '/pcnt/subscription_base_price/');
    return res.data ? res.data : [];
}

const getUniqueCrn = async (): Promise<string[]> => {
    const data = await getBasePrice();
    
    // Создаем массив всех crn, затем убираем дубликаты через Set
    return [...new Set(data.map(item => item.crn))];
};

const getCameraCatg = async (): Promise<CameraCatg[]> => {
    const res = await axios.get(basename + '/billing/camera_catg/');
    return res.data ? res.data : [];
}

const getSubscription  = async (customer_id: number): Promise<boolean> => {
    
    console.log('getSubscription for customer_id:', customer_id);
    
    const res = await axios.get(`/billing/get_subscription/?customer_id=${customer_id}`);
    return (res.data?.subscription_state || 'n/d') == 'active';
}

const suspendService = async (customer_id: number): Promise<boolean> => {
    try {
        const res = await axios.get(`/billing/unsubscribe/?customer_id=${customer_id}`);
        return res.status === 200;
    } catch (err) {
        console.error('[Balance API] Error during cancelService:', err);
        return false;
    }
}

const printOrder = async (orderId: number): Promise<any> =>  {
    try {
        const res = await axios.get(`/billing/print_order/?order_id=${orderId}`, {
            responseType: 'blob',
        });
        return res.data;
    } catch {
        return null;
    }
}

export interface PcntOrder {
    customer_id: number;
    analytics_type: number;
    order_count: number;
    datasource: string;
}

const getPcntOrderQtty = async (param: {analytics_type: number,} ): Promise<number> => {
    const res = await axios.get(`/pcnt/pcnt_order/?analytics_type=${param.analytics_type}`); 
    return res.data.length > 0 ? res.data[0].order_count : 0;
}

const getPcntOrderQttyAll = async (): Promise<PcntOrder[]> => {
    const res = await axios.get('/pcnt/pcnt_order/'); 
    return res.data;
}

export interface PreInvoiceParam {
    camerasAnalytics: number;
    camerasSimple: number;
    periodMon: number;
    currency: string;
}

export interface PreInvoiceResult {
    activate: boolean;
    amount: number;
    discount: number;
    total: number;
    end_date: Date;
    order_id: number;
}

const getPreInvoice = async (param: PreInvoiceParam): Promise<PreInvoiceResult | null> => {
    try {
        const {camerasAnalytics, camerasSimple, periodMon, ...pure_param} = param;
        const res = await axios.post('/billing/pre_invoice/',  {
            ...pure_param, 
            app_id: packageJson.name,
            cameras: {
                [CameraAnalyticsType.ON_BOARD]: camerasAnalytics, 
                [CameraAnalyticsType.SYSTEM]: camerasSimple
            },
            period_mon: periodMon,
            pay_type: 'bank',
            activate: false, 
        });
        const {end_date, amount_discount, ...data} = res.data;
        return {...data, end_date: new Date(end_date), discount: amount_discount};
    } catch (error) {
        console.error('[Billing API] Error during getPreInvoice:', error);
        return null;
    }
}

const postPreInvoice = async (param: PreInvoiceParam): Promise<PreInvoiceResult | null> => {
    try {
        const {camerasAnalytics, camerasSimple, periodMon, ...pure_param} = param;
        const res = await axios.post('/billing/pre_invoice/',  {
            ...pure_param, 
            app_id: packageJson.name,
            cameras: {
                [CameraAnalyticsType.ON_BOARD]: camerasAnalytics, 
                [CameraAnalyticsType.SYSTEM]: camerasSimple
            },
            period_mon: periodMon,
            // pay_type: 'bank',
            param: {},
            activate: true,
        });
        const {end_date, ...data} = res.data;
        return {...data, end_date: new Date(end_date)};
    } catch (error) {
        console.error('[Billing API] Error during postPreInvoice:', error);
        return null;
    }
}

export interface PreInvoiceNoPayParam {
    camerasAnalytics: number;
    camerasSimple: number;
}

export interface PreInvoiceNoPayResult {
    end_date: Date;
}

const getPreInvoiceNoPay = async (param: PreInvoiceNoPayParam): Promise<PreInvoiceNoPayResult | null> => {
    try {
        const {camerasAnalytics, camerasSimple, ...pure_param} = param;
        const res = await axios.post('/billing/pre_invoice/', {
            ...pure_param, 
            app_id: packageJson.name,
            cameras: {
                [CameraAnalyticsType.ON_BOARD]: camerasAnalytics, 
                [CameraAnalyticsType.SYSTEM]: camerasSimple
            },
            pay_type: 'no_pay',
            activate: false,
        });
        const {end_date, ...data} = res.data;
        return {...data, end_date: new Date(end_date)};
    } catch (error) {
        console.error('[Billing API] Error during getPreInvoiceNoPay:', error);
        return null;
    }
}

const postPreInvoiceNoPay = async (param: PreInvoiceNoPayParam): Promise<PreInvoiceNoPayResult | null> => {
    try {
        const {camerasAnalytics, camerasSimple, ...pure_param} = param;
        const res = await axios.post('/billing/pre_invoice/', {
            ...pure_param, 
            app_id: packageJson.name,
            cameras: {
                [CameraAnalyticsType.ON_BOARD]: camerasAnalytics, 
                [CameraAnalyticsType.SYSTEM]: camerasSimple
            },
            pay_type: 'no_pay',
            activate: true,
        });
        const {end_date, ...data} = res.data;
        return {...data, end_date: new Date(end_date)};
    } catch (error) {
        console.error('[Billing API] Error during postPreInvoiceNoPay:', error);
        return null;
    }
}

export const api = {
    getBalance,
    // createOrder,
    // createOrder2,
    // createOrderMonobank,
    getOrderStatus,
    // getBasePrice,
    getUniqueCrn,
    getCameraCatg,
    getSubscription, 
    suspendService,
    // getCamerasFact,
    getCameras,
    // getCamerasAnalytics,
    // getCamerasSimple,
    printOrder,
    // getDiscountPrepaiment,
    // getDiscountQuantity,
    // getPcntOrder,
    getPcntOrderQtty,
    getPcntOrderQttyAll,
    // savePcntOrder,
    getPreInvoice,
    getPreInvoiceNoPay,
    postPreInvoice,
    postPreInvoiceNoPay,
}